import { useState } from "react";
import { TENANT_DOMAIN } from "../../utils/domain";
import { ERROR_MESSAGES } from "../../constant/constant";
import { useAxios } from "../../utils/useAxios";

/**
 * @typedef {Object} QuizLimitStatusData
 * @property {string} mode - The quiz limit mode (e.g., "school")
 * @property {number|null} school_limit - The school-level quiz limit, or null if not set
 * @property {boolean} teacher_decides - Whether teachers can set their own limits
 * @property {boolean} classroom_overrides_exist - Whether any classroom-level overrides exist
 * @property {boolean} student_overrides_exist - Whether any student-level overrides exist
 */

/**
 * @description Custom hook to fetch quiz limit status
 */
export const useGetQuizLimitStatus = () => {
    const [statusData, setStatusData] = useState(
    /** @type {QuizLimitStatusData | null | undefined} */(undefined),
    );
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const { axiosInstance } = useAxios();

    /**
     * Fetch quiz limit status
     * @async
     * @param {Object} [params] - Optional parameters
     * @param {number|string} [params.classroom_id] - Optional classroom ID to get status for a specific classroom
     * @returns {Promise<void>}
     */
    const getQuizLimitStatus = async (params) => {
        try {
            setLoading(true);
            setError("");

            let url = `${TENANT_DOMAIN}/quiz/limit/status`;
            if (params?.classroom_id) {
                url += `?classroom_id=${params.classroom_id}`;
            }
            const response = await axiosInstance.get(url);

            setStatusData(response?.data || null);
        } catch (err) {
            setStatusData(null);

            if (err?.response?.status === 500) {
                setError(ERROR_MESSAGES.INTERNAL_SERVER);
            } else {
                setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
            }
        } finally {
            setLoading(false);
        }
    };

    return {
        statusData,
        loading,
        error,
        getQuizLimitStatus,
    };
};
